import { DateTime } from "luxon";
import {
  Area,
  AreaChart,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import {
  NameType,
  Payload,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";

const TooltipContainer = styled.div`
  background-color: #dfb140;
  padding: 10px;
  border-radius: 5px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
`;

const TooltipText = styled.p`
  font-family: "Lato", sans-serif;
  color: #000000;
  font-size: 14px;
  margin: 0;
`;

const ChartContainer = styled.div`
  font-family: "Oswald", sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ChartTitleContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: #406edf;
  color: #ffffff;
  padding: 5px 15px;
  border-radius: 5px;
  font-weight: bold;
  margin-bottom: 20px;
`;

const ChartTitle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #1e1e1e;
  margin-bottom: 10px;
`;

const ChartTitlePrefix = styled.span`
  font-size: 16px;
`;

const IconTitle = styled.div`
  font-size: 18px;
  margin-left: 5px;
  margin-top: 3px;
`;

const ChartTitlePredictionSmall = styled.h2`
  font-family: "Lato", sans-serif;
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  text-align: center;
`;

const ChartTitlePrediction = styled.h2`
  font-family: "Lato", sans-serif;
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  text-align: center;
  text-transform: uppercase;


  font-family: 'Oswald';
  font-style: normal;
  font-weight: 600;
  font-size: 28px;
  line-height: 120%;
  text-align: center;
  color: #1A1A1A;

  @media (min-width: 768px) {
    font-size: 54px;
  }
`;

const ChartTitlePredictionDate = styled.span`
  color: #0B6DFF;
`;

type ChartData = {
  date: DateTime;
};

enum TitleStyle {
  Text,
  Icon,
}

// Dates on the charts read the active language in Spanish ("3 de feb");
// English keeps luxon's default locale, exactly as before.
const isSpanishChart = (): boolean => i18n.language === "es";

const chartDate = (date: DateTime): DateTime =>
  isSpanishChart() ? date.setLocale("es-MX") : date;

/** "d MMM" style date ("30 Jun 2027"); Spanish "30 de jun de 2027". */
const formatShortChartDate = (date: DateTime, withYear: boolean): string =>
  isSpanishChart()
    ? chartDate(date).toFormat(`d 'de' MMM${withYear ? " 'de' yyyy" : ""}`)
    : date.toFormat(`d MMM${withYear ? " yyyy" : ""}`);

const formatDate = (date: number): string => {
  return chartDate(DateTime.fromMillis(date)).toFormat("MMM");
};

const getUniqueMonthTicks = (data: ChartData[]): number[] => {
  const ticksByMonth = new Map<string, number>();

  data.forEach(({ date }) => {
    const month = `${date.year}-${date.month}`;
    if (!ticksByMonth.has(month)) {
      ticksByMonth.set(month, date.toMillis());
    }
  });

  const finalDate = data[data.length - 1]?.date;
  if (finalDate) {
    ticksByMonth.set(`${finalDate.year}-${finalDate.month}`, finalDate.toMillis());
  }

  return Array.from(ticksByMonth.values());
};

const CustomLabel = (props: any) => {
  const { viewBox, label, location, caret, bendMode } = props;
  const { x: viewBoxX, y: viewBoxY, height: viewBoxHeight } = viewBox;

  const width = label.length * 5.66 + 30;
  const height = 30;
  const padding = 8;
  const ovalWidth = width + 2 * padding;
  const ovalHeight = height + 2 * padding;

  const bendInset = 16;
  const xOffset = bendMode
    ? location === "top" ? bendInset : -width - bendInset
    : -ovalWidth - 0;
  const yOffset = 180;

  const startPointX = viewBoxX + xOffset + 10;
  const startPointY =
    location === "top" ? viewBoxHeight - 60 : viewBoxY + yOffset + 50;

  // Determine the end point of the arrow
  const endPointX = viewBoxX * 0.98;
  const endPointY = location === "top" ? 40 : 225;

  // Control point for the curve
  const controlPointX = (startPointX + endPointX) / 2 + 10;
  const controlPointY = (startPointY + endPointY) / 2;

  const arrowPath = `M${startPointX},${startPointY} Q${controlPointX},${controlPointY} ${endPointX},${endPointY}`;

  const yLocation = bendMode
    ? viewBoxY + bendInset
    : location === "top" ? viewBoxY + 30 : viewBoxY + yOffset;
  const isUp = caret === "▲";
  const caretColor = isUp ? "#6aa84f" : "#ea552b";
  const arrowColor = isUp ? "#535353" : "#787878";

  // return (
  //   <g>
  //     <defs>
  //       <marker
  //         id="arrow"
  //         markerWidth="10"
  //         markerHeight="10"
  //         refX="0"
  //         refY="3"
  //         orient="auto"
  //         markerUnits="strokeWidth"
  //       >
  //         <path d="M0,0 L0,6 L9,3 z" fill={arrowColor} />
  //       </marker>
  //     </defs>
  //     <ellipse
  //       cx={viewBoxX + xOffset}
  //       cy={yLocation}
  //       rx={ovalWidth / 2}
  //       ry={ovalHeight / 1.7}
  //       fill="#DFB140"
  //     />
  //     <text
  //       x={viewBoxX + xOffset}
  //       y={yLocation + 3}
  //       textAnchor="middle"
  //       alignmentBaseline="middle"
  //       fill="#000"
  //     >
  //       {label}
  //       <tspan fill={caretColor} dx="2" dy="4">
  //         {caret}
  //       </tspan>
  //     </text>
  //     <path
  //       d={arrowPath}
  //       fill="transparent"
  //       stroke={arrowColor}
  //       strokeWidth="3"
  //       markerEnd="url(#arrow)"
  //     />
  //   </g>
  // );

  return (

      <g className="chart-chip-container">
        <rect width={width} height="39" rx="5" fill="white" fill-opacity="0.8" x={viewBoxX + xOffset} y={yLocation} />
        <text id="&#240;&#159;&#142;&#175;" fill="#1A1A1A" style={{ whiteSpace: "pre" }} font-family="Lato" font-size="15" letter-spacing="0em"><tspan dx='5' dy='23' x={viewBoxX + xOffset} y={yLocation}>&#x1f3af;</tspan></text>
        <text id="12% BF by Sept 25" fill="#1A1A1A" style={{ whiteSpace: "pre" }} font-family="Lato" font-size="12" font-weight="bold" letter-spacing="0em"><tspan dx='25' dy='23' x={viewBoxX + xOffset} y={yLocation}>{label}</tspan></text>
      </g>

  );
};

type CustomTooltipProps = {
  active?: boolean;
  payload?: Payload<ValueType, NameType>[];
};

type ChartProps = {
  data: ChartData[];
  dataKey: string;
  showTitle?: TitleStyle;
  icon: IconDefinition;
  titlePrefix?: string;
  titlePrediction?: string; // only shows up if showTitle is set to TitleStyle.Text
  titlePredictionDate?: string; // only shows up if showTitle is set to TitleStyle.Text
  CustomTooltip: React.FC<CustomTooltipProps>;
  finalMarker?: string | JSX.Element;
  caret: "▼" | "▲";
  markerLocation: "top" | "bottom";
  yAxisLabel?: string;
  showOnlyTitle?: boolean;
  showBigHeader?: boolean;
  dedupeMonthTicks?: boolean;
  bendMode?: boolean;
};

const Chart = (props: ChartProps) => {
  const {
    data,
    dataKey,
    showTitle,
    icon,
    titlePrefix,
    titlePrediction,
    titlePredictionDate,
    caret,
    finalMarker,
    yAxisLabel,
    markerLocation,
    showOnlyTitle,
    showBigHeader = false,
    dedupeMonthTicks,
    bendMode,
  } = props;
  const [isAnimationEnd, setIsAnimationEnd] = useState(false);
  // Re-render (month ticks) when the language changes.
  useTranslation();
  const chartdata = data.map((d) => ({ ...d, date: d.date.toMillis() }));
  const monthTicks = useMemo(
    () => dedupeMonthTicks ? getUniqueMonthTicks(data) : undefined,
    [data, dedupeMonthTicks],
  );

  const PredictionComponent = showBigHeader ? ChartTitlePrediction : ChartTitlePredictionSmall;

  const title = useMemo(() => {
    switch (showTitle) {
      case TitleStyle.Text:
        return (
          <ChartTitle>
            <PredictionComponent>
              {titlePrefix} {" "}
              {titlePrediction} {" "}
              {titlePredictionDate && <ChartTitlePredictionDate>{` ${titlePredictionDate}`}</ChartTitlePredictionDate>}
              {titlePredictionDate && "!"}
            </PredictionComponent>
          </ChartTitle>
        );
      case TitleStyle.Icon:
        return (
          <ChartTitleContainer>
            <FontAwesomeIcon icon={icon} />
            <IconTitle>{titlePrefix}</IconTitle>
          </ChartTitleContainer>
        );
      default:
        return <></>;
    }
  }, [icon, showTitle, titlePrefix, titlePrediction, titlePredictionDate]);

  const marker = useMemo(() => {
    if (finalMarker) {
      return (
        <ReferenceLine
          x={bendMode && markerLocation === "top" ? chartdata[0].date : chartdata[chartdata.length - 1].date}
          // segment={[{ x: chartdata[chartdata.length - 1].date, y: 0 }, { x: chartdata[chartdata.length - 1].date, y: 0 }]}
          stroke="#FF0000"
          strokeWidth={0}
          label={
            <CustomLabel
              label={finalMarker}
              caret={caret}
              location={markerLocation}
              bendMode={bendMode}
            />
            // <div></div>
          }
        />
      );
    }
    return <></>;
  }, [chartdata, finalMarker, markerLocation, caret, bendMode]);

  if (showOnlyTitle) {
    return <ChartContainer>
      {title}
    </ChartContainer>;
  }

  return (
    <ChartContainer className={`${isAnimationEnd ? 'animation-end' : ''}`}>
      {title}
      <ResponsiveContainer width={"90%"} height={300}>
        <AreaChart
          data={chartdata}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id="colorWeight" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#406EDF" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#406EDF" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="date"
            type="number"
            tickFormatter={formatDate}
            domain={["dataMin, dataMax"]}
            scale={"time"}
            {...(monthTicks ? { ticks: monthTicks } : {})}
          />
          <YAxis
            width={55}
            type="number"
            domain={["dataMin", "dataMax"]}
            tickCount={4}
            label={{ value: yAxisLabel, angle: -90, position: "insideLeft" }}
            scale={"linear"}
          />
          {/* <Tooltip content={payload => <CustomTooltip {...payload} />} wrapperStyle={{ border: 'none' }} /> */}
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke="#406EDF"
            strokeWidth={4}
            fillOpacity={1}
            fill="url(#colorWeight)"
            onAnimationEnd={() => setIsAnimationEnd(true)}
          />
          {marker}
        </AreaChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

export { Chart, chartDate, formatShortChartDate, getUniqueMonthTicks, TooltipContainer, TooltipText, TitleStyle };
export type { ChartData, CustomTooltipProps };
